import {useEffect, useState} from 'react';
import LinkOffIcon from '@mui/icons-material/LinkOff';

import {CallEndReason} from './types';

import styles from './videoCall.module.scss';

type Props = {
    show: boolean;
    onClose?: () => void;
    countDown?: number;
};

// Count down before auto close
const useCloseCountDown = ({onClose, countDown}: {onClose?: () => void; countDown?: number}) => {
    const [remaining, setRemaining] = useState<number | null>(countDown != null && onClose ? countDown : null);

    useEffect(() => {
        if (remaining === null) return;
        if (remaining <= 0) {
            onClose?.();
            return;
        }
        const id = setTimeout(() => setRemaining((r) => (r !== null ? r - 1 : null)), 1000);
        return () => clearTimeout(id);
    }, [remaining, onClose]);

    return remaining;
};

type BannerAutoCloseFooterProps = {
    onClose?: () => void;
    countDown?: number;
};

const BannerAutoCloseFooter = ({onClose, countDown}: BannerAutoCloseFooterProps) => {
    const remaining = useCloseCountDown({onClose, countDown});

    if (!onClose) return null;

    return (
        <div className={styles.bannerAutoCloseFooter}>
            <button className={styles.expiredBannerBtn} onClick={onClose}>
                Close widget
            </button>
            {remaining !== null && <span className={styles.bannerCountDown}>Closing in {remaining}s</span>}
        </div>
    );
};

const BannerCallSessionExpired = ({show, onClose, countDown}: Props) => {
    if (!show) return null;

    return (
        <div className={styles.expiredBanner} role="alert">
            <LinkOffIcon style={{fontSize: 28, flexShrink: 0}} />
            <div className={styles.expiredBannerBody}>
                <strong className={styles.expiredBannerTitle}>Call link has expired</strong>
                <span className={styles.expiredBannerHint}>
                    This room ID is no longer valid. Please generate a new call link to continue.
                </span>
            </div>
            <BannerAutoCloseFooter onClose={onClose} countDown={countDown} />
        </div>
    );
};

const BannerNoParticipantsAutoDisconnect = ({show, onClose, countDown}: Props) => {
    if (!show) return null;

    return (
        <div className={styles.expiredBanner} role="alert">
            <LinkOffIcon style={{fontSize: 28, flexShrink: 0}} />
            <div className={styles.expiredBannerBody}>
                <strong className={styles.expiredBannerTitle}>Call ended due to inactivity</strong>
                <span className={styles.expiredBannerHint}>
                    The call has been disconnected because there were no participants for a while.
                </span>
            </div>
            <BannerAutoCloseFooter onClose={onClose} countDown={countDown} />
        </div>
    );
};

export const EndCallBanner = ({reason, onClose}: {reason: CallEndReason | null; onClose?: () => void}) => {
    const countDown: number | null = null; // seconds before auto close the banner

    switch (reason) {
        case CallEndReason.ExpiredLink:
            return <BannerCallSessionExpired show={true} onClose={onClose} countDown={countDown} />;
        case CallEndReason.NoParticipants:
            return <BannerNoParticipantsAutoDisconnect show={true} onClose={onClose} countDown={countDown} />;
        default:
            return null;
    }
};
